import { Notification } from '@mantine/core'
import { IconInfoCircle, IconX } from '@tabler/icons-react'
import { Typography } from './Typography'

export type SnackbarProps = {
  message: string
  /** タイトル。指定時はタイトル＋メッセージのレイアウト（アイコンなし） */
  title?: string
  onClose?: () => void
  actionLabel?: string
  onAction?: () => void
}

function InfoIcon({ size }: { size: number }) {
  return <IconInfoCircle size={size} stroke={1.5} color="white" aria-hidden="true" />
}

/** 閉じる（X）ボタン */
function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="閉じる"
      className="flex size-6 shrink-0 items-center justify-center text-white hover:opacity-70"
    >
      <IconX size={16} stroke={1.5} />
    </button>
  )
}

export function Snackbar({ message, title, onClose, actionLabel, onAction }: SnackbarProps) {
  // タイトルありのときはアイコンを表示しない
  const showIcon = !title

  return (
    <Notification
      unstyled
      withBorder={false}
      icon={showIcon ? <InfoIcon size={36} /> : null}
      onClose={onClose}
      withCloseButton={false}
      classNames={{
        // 親コンテナの px-4 に収まるよう幅は 100%。背景 rgba(0,0,0,0.7) / 角丸 6px / 左右 12・8px
        root: `relative flex w-full box-border rounded-md bg-black/70 px-4 py-6 ${
          title ? 'items-start min-h-[55px] py-6' : 'items-center h-[55px]'
        }`,
        icon: 'flex items-center justify-center shrink-0 mr-1 bg-transparent',
        body: 'flex-1 min-w-0 flex items-center',
        // children を包む description スロットも幅いっぱいに（justify-between を効かせるため）
        description: 'w-full min-w-0 flex-1',
      }}
    >
      {title ? (
        <div className="flex w-full flex-col gap-2">
          {/* タイトル行: 左にタイトル・右端に X（同じ高さ） */}
          <div className="flex items-center justify-between gap-2">
            <Typography
              variant="caption1"
              component="p"
              className="min-w-0 whitespace-pre-wrap text-white"
              style={{ fontSize: 16, fontWeight: 700 }}
            >
              {title}
            </Typography>
            {onClose && <CloseButton onClose={onClose} />}
          </div>
          <Typography variant="caption1" component="p" className="min-w-0 flex-1 whitespace-pre-wrap text-white">
            {message}
          </Typography>
        </div>
      ) : (
        <div className="flex w-full items-center justify-between gap-2">
          <Typography
            variant="caption1"
            component="span"
            className="min-w-0 whitespace-pre-wrap text-white"
            style={{ fontSize: 14 }}
          >
            {message}
          </Typography>
          {/* 右端グループ: アクションボタン＋X */}
          {(actionLabel || onClose) && (
            <div className="flex shrink-0 items-center gap-2">
              {actionLabel && (
                <button
                  type="button"
                  onClick={onAction}
                  className="h-[31px] w-[100px] whitespace-nowrap rounded-[8px] border border-[#757575] bg-white px-4 text-[12px] font-bold leading-4 text-[#757575] hover:opacity-85"
                >
                  {actionLabel}
                </button>
              )}
              {onClose && <CloseButton onClose={onClose} />}
            </div>
          )}
        </div>
      )}
    </Notification>
  )
}
